import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// Lepivý obsah (Stitch redizajn 2026-07-20) — rovnaký vzor ako MetodikaPage.tsx.
function useScrollSpy(keys: string[]): string | null {
  const [active, setActive] = useState<string | null>(null);
  useEffect(() => {
    if (keys.length === 0) return;
    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        const first = keys.find((k) => visible.has(k));
        if (first) setActive(first);
      },
      { rootMargin: '-12% 0px -70% 0px', threshold: 0 },
    );
    for (const key of keys) {
      const el = document.getElementById(key);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [keys.join('|')]);
  return active;
}

const TOC_ITEMS = [
  { key: 'auth', label: 'Autentifikácia' },
  { key: 'limity', label: 'Limity' },
  { key: 'endpointy', label: 'Endpointy' },
  { key: 'priklad', label: 'Príklad odpovede' },
  { key: 'badge', label: 'Embeddable odznak' },
];

interface Endpoint {
  method: string;
  path: string;
  desc: string;
}

const endpoints: Endpoint[] = [
  {
    method: 'GET',
    path: '/public/companies/search?q=',
    desc: 'Fulltextové hľadanie firiem podľa názvu',
  },
  { method: 'GET', path: '/public/companies/:ico', desc: 'Profil firmy (RPO)' },
  {
    method: 'GET',
    path: '/public/companies/:ico/financials',
    desc: 'Viacročné účtovné výkazy (RÚZ)',
  },
  {
    method: 'GET',
    path: '/public/companies/:ico/financial-summary',
    desc: 'Ročný súhrn kľúčových metrík',
  },
  {
    method: 'GET',
    path: '/public/companies/:ico/risk-score',
    desc: 'Rizikové skóre + Altman Z" a signály',
  },
  {
    method: 'GET',
    path: '/public/companies/:ico/network',
    desc: 'Graf prepojení osôb↔firiem (?depth=1-4)',
  },
  {
    method: 'GET',
    path: '/public/companies/:ico/persons',
    desc: 'Štatutári, orgány, koneční užívatelia výhod',
  },
  {
    method: 'GET',
    path: '/public/companies/:ico/disqualifications',
    desc: 'Diskvalifikácia osôb vo firme',
  },
  { method: 'GET', path: '/public/companies/:ico/similar', desc: 'Podobné firmy (rovnaké mesto)' },
  {
    method: 'GET',
    path: '/public/companies-bulk-risk-score?icos=',
    desc: 'Skóre pre viac IČO naraz (čiarkou oddelené, max 50)',
  },
];

// /dokumentacia-api — Blok 4, Týž. 16. Self-service dokumentácia pre externých
// integrátorov, žiadny gate (aj bez účtu vidno, čo API vie). Kľúč sa generuje
// v /ucet (AccountPage → ApiKeysSection).
export function ApiDocsPage() {
  const active = useScrollSpy(TOC_ITEMS.map((i) => i.key));
  return (
    <main className="page page-with-toc">
      <div className="page-header">
        <h1>API dokumentácia</h1>
        <p>Programatický prístup k rovnakým dátam ako vo webe — zadarmo, s API kľúčom.</p>
      </div>

      <div className="doc-toc-layout">
        <nav className="section-rail" aria-label="Obsah stránky">
          <div className="rail-label">Obsah</div>
          {TOC_ITEMS.map((item) => (
            <a key={item.key} href={`#${item.key}`} className={active === item.key ? 'on' : ''}>
              {item.label}
            </a>
          ))}
        </nav>

        <div>
          <section id="auth">
            <div className="section-title">
              <h2>Autentifikácia</h2>
            </div>
            <div className="card">
              <p style={{ margin: '0 0 0.5rem', fontSize: '0.88rem', color: 'var(--ink)' }}>
                Vygeneruj si API kľúč v <Link to="/ucet">Účte</Link> a posielaj ho v hlavičke{' '}
                <code>Authorization</code> na každom volaní:
              </p>
              <pre className="code-block">{`curl https://radar.marstein.sk/api/v1/public/companies/57715807 \\
  -H "Authorization: Bearer mr_live_..."`}</pre>
            </div>
          </section>

          <section id="limity">
            <div className="section-title">
              <h2>Limity</h2>
            </div>
            <div className="card">
              <p style={{ margin: 0, fontSize: '0.88rem', color: 'var(--ink)' }}>
                Free plán: <b>200 požiadaviek / deň</b>. Pro plán: <b>10 000 požiadaviek / deň</b>.
                Aktuálny stav sa vracia v hlavičkách odpovede (<code>X-RateLimit-Limit</code>,{' '}
                <code>X-RateLimit-Remaining</code>, <code>X-RateLimit-Reset</code>). Po prekročení
                limitu API vráti <code>429</code> až do resetu (denne o polnoci UTC). Samotné dáta
                (profil, financie, risk skóre, graf prepojení) sú vždy zadarmo — limit sa týka len
                objemu programatického prístupu, nie funkcií.
              </p>
            </div>
          </section>

          <section id="endpointy">
            <div className="section-title">
              <h2>Endpointy</h2>
            </div>
            <div className="card">
              {endpoints.map((e) => (
                <div className="endpoint-row" key={e.path}>
                  <span className="method">{e.method}</span>
                  <span className="endpoint-path">{e.path}</span>
                  <span className="desc">{e.desc}</span>
                </div>
              ))}
            </div>
          </section>

          <section id="priklad">
            <div className="section-title">
              <h2>Príklad odpovede</h2>
            </div>
            <div className="card">
              <pre className="code-block">{`GET /api/v1/public/companies/57715807/risk-score

{
  "ico": "57715807",
  "score": 12,
  "band": "low",
  "signals": [ ... ]
}`}</pre>
            </div>
          </section>

          <section id="badge">
            <div className="section-title">
              <h2>Embeddable odznak (risk-badge)</h2>
              <span className="hint">bez API kľúča, žiadny JS</span>
            </div>
            <div className="card">
              <p style={{ margin: '0 0 0.5rem', fontSize: '0.88rem', color: 'var(--ink)' }}>
                Malý samostatný HTML odznak s rizikovým skóre firmy, vhodný na vloženie na vlastný
                web cez <code>&lt;iframe&gt;</code> — žiadny JavaScript, žiadny API kľúč. Funguje
                len pre firmy, ktoré už niekedy niekto vyhľadal na Corpuls.
              </p>
              <pre className="code-block">{`<iframe
  src="https://radar.marstein.sk/api/v1/embed/57715807/risk-badge"
  width="300" height="90" style="border:none"
></iframe>`}</pre>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
